import React from "react";
import { Table, TableProps } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { SorterResult } from "antd/es/table/interface";
import { IRankingSnapshotRow } from "modules/organization/models/interface";
import { mnDate } from "modules/shared/services";
import { dateFormat } from "config/commonConfig";
import { useRankingSnapshotStore } from "../store/rankingSnapshotStore";

interface RankingSnapshotTableViewProps {
  rows: IRankingSnapshotRow[];
  loading: boolean;
  rankingType?: "template" | "point";
  onOpenRecord?: (record: IRankingSnapshotRow) => void;
}

const RankingSnapshotTableView: React.FC<RankingSnapshotTableViewProps> = ({
  rows,
  loading,
  rankingType,
  onOpenRecord,
}) => {
  const { sortColumn, sortOrder, setSort } = useRankingSnapshotStore();

  const defaultSortOrderOf = (column: string) =>
    sortColumn === column ? sortOrder : null;

  const columns: ColumnsType<IRankingSnapshotRow> = [
    {
      title: "RECORD",
      dataIndex: "record_name",
      sorter: true,
      defaultSortOrder: defaultSortOrderOf("record"),
      render: (data, record) => (
        <span>
          {record.record_type ? `(${record.record_type}) ` : ""}
          {data}
        </span>
      ),
    },
    {
      title: "EFFECTIVE",
      dataIndex: "effective_from",
      align: "right",
      width: 110,
      sorter: true,
      defaultSortOrder: defaultSortOrderOf("effective"),
      render: (data) => (data ? mnDate(data).format(dateFormat.default) : ""),
    },
    {
      title: "THROUGH",
      dataIndex: "through_to",
      align: "right",
      width: 110,
      sorter: true,
      defaultSortOrder: defaultSortOrderOf("through"),
      render: (data) => (data ? mnDate(data).format(dateFormat.default) : ""),
    },
    {
      title: "SERIES",
      dataIndex: "series_name",
      width: 270,
      sorter: true,
      defaultSortOrder: defaultSortOrderOf("series"),
    },
    ...(rankingType === "template"
      ? ([
          {
            title: "RANK",
            dataIndex: "rank_name",
            width: 150,
            sorter: true,
            defaultSortOrder: defaultSortOrderOf("rank"),
          },
        ] as ColumnsType<IRankingSnapshotRow>)
      : []),
    ...(rankingType === "point"
      ? ([
          {
            title: "POINTS",
            dataIndex: "points",
            width: 110,
            align: "right",
            sorter: true,
            defaultSortOrder: defaultSortOrderOf("points"),
          },
        ] as ColumnsType<IRankingSnapshotRow>)
      : []),
    {
      title: "ACTION",
      dataIndex: "record_id",
      width: 70,
      align: "center",
      render: (_data, record) => (
        <ArrowRightOutlined
          role="img"
          aria-label={`Open ${record.record_name}`}
          onClick={() => onOpenRecord?.(record)}
        />
      ),
    },
  ];

  const columnKeyToSortColumn: Record<string, string> = {
    record_name: "record",
    effective_from: "effective",
    through_to: "through",
    series_name: "series",
    rank_name: "rank",
    points: "points",
  };

  const onChange: TableProps<IRankingSnapshotRow>["onChange"] = (
    _pagination,
    _filters,
    sorter,
  ) => {
    const { field, order } = sorter as SorterResult<IRankingSnapshotRow>;
    const column = field ? columnKeyToSortColumn[field.toString()] : null;
    if (column && order) {
      setSort(column as any, order);
    } else {
      setSort(null, null);
    }
  };

  return (
    <Table
      columns={columns}
      dataSource={rows}
      rowKey={(record) => record.record_id}
      pagination={false}
      loading={loading}
      onChange={onChange}
      showSorterTooltip={false}
      className="scrollableTable"
    />
  );
};

export default RankingSnapshotTableView;
